import { ThemeHuePicker } from 'components/ThemeHuePicker/ThemeHuePicker'
import Styles from './InterfaceColorSettings.module.scss'

import { InterfaceColorSettingsProps } from './Types'
import { observer } from 'mobx-react-lite'
import client from 'api/client'
import { reportError } from 'utils/error'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useDispatch } from 'react-redux'
import { updateWorkspaceColorScheme } from 'redux/actions/workspace'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { WorkspaceColorScheme } from 'types'
import { useDefaultTheme } from 'hooks/useDefaultTheme'

const THEMES: Record<WorkspaceColorScheme, string> = {
  light: '#FFFFFF',
  teal: '#09E19C',
  blue: '#0D95F8',
  green: '#52DB4E',
  yellow: '#F6D43D',
  red: '#FA6F6B',
  magenta: '#DE6BFA',
  purple: '#6B7EFA',
  gray: '#99B7C9',
}

export const DEFAULT_THEME = 'light'

const InterfaceColorSettings = observer(({ workspace }: InterfaceColorSettingsProps) => {
  const defaultTheme = useDefaultTheme()
  const scheme = workspace.color_scheme ?? defaultTheme
  const dispatch = useDispatch()

  const themes = Object.values(THEMES)

  const updateScheme = async (scheme: WorkspaceColorScheme) => {
    const oldColorScheme = workspace.color_scheme
    try {
      dispatch(updateWorkspaceColorScheme({ workspaceKey: workspace.key, color_scheme: scheme }))

      const response = await client.updateWorkspaceSettings({ workspace: { color_scheme: scheme } }, workspace.key)
      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        dispatch(updateWorkspaceColorScheme({ workspaceKey: workspace.key, color_scheme: oldColorScheme }))
      }
    } catch (error) {
      dispatch(updateWorkspaceColorScheme({ workspaceKey: workspace.key, color_scheme: oldColorScheme }))
      showToastMessage('There was an error updating the theme. Our team has been notified.', 'error')
      reportError(error, { metaData: { workspaceKey: workspace.key, colorScheme: scheme } })
    }
  }

  return (
    <div className={Styles.section}>
      <Heading level={3}>Interface Color</Heading>
      <Text variant="secondary" color="var(--dark-60)">
        Changing this color would affect how Blaze.ai looks like to everyone in this workspace.
      </Text>
      <ThemeHuePicker
        className={Styles.picker}
        colors={themes}
        value={THEMES[scheme]}
        onChange={(hex) => {
          const scheme = Object.entries(THEMES).find(([_, schemeHex]) => hex === schemeHex)?.[0]
          if (!scheme) return
          updateScheme(scheme as WorkspaceColorScheme)
        }}
      />
    </div>
  )
})

export { InterfaceColorSettings }
